import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as actions from '../../store/onboardingV2/actions.ts';
import * as thunks from '../../store/onboardingV2/thunks.ts';
import { getStepFromQuery } from '../../utils/onboardingV2/onboardingNavigation.ts';

export const useOnboardingFlow = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const state = useSelector((state: any) => state.onboardingV2);
  
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam === 'summary') {
      const stepFromQuery = getStepFromQuery(searchParams);
      if (stepFromQuery !== state.currentStep) {
        dispatch(actions.setStep(stepFromQuery as any));
      }
    }
  }, [searchParams, state.currentStep, dispatch]);
  
  const loadSkillsAndCategories = useCallback(() => {
    return dispatch(thunks.loadOnboardingSkillsAndCategories() as any);
  }, [dispatch]);
  
  const getCities = useCallback((provinceId: number) => {
    return dispatch(thunks.getCitiesV2(provinceId) as any);
  }, [dispatch]);
  
  const updateProfile = useCallback(() => {
    return dispatch(thunks.updateUserProfileV2() as any);
  }, [dispatch]);
  
  const setAccountType = useCallback((type: 'PERSON' | 'COMPANY') => {
    dispatch(actions.setAccountType(type));
  }, [dispatch]);
  
  const setPersonaForm = useCallback((formData: any) => {
    dispatch(actions.setPersonaForm(formData));
  }, [dispatch]);
  
  const setEmpresaForm = useCallback((formData: any) => {
    dispatch(actions.setEmpresaForm(formData));
  }, [dispatch]);
  
  const setUserSkills = useCallback((skills: any[]) => {
    dispatch(actions.setUserSkills(skills));
  }, [dispatch]);
  
  const addSkill = useCallback((skill: any) => {
    dispatch(actions.addSkill(skill));
  }, [dispatch]);
  
  const removeSkill = useCallback((skillId: number) => {
    dispatch(actions.removeSkill(skillId));
  }, [dispatch]);
  
  const setWorkZones = useCallback((zones: any[]) => {
    dispatch(actions.setWorkZones(zones));
  }, [dispatch]);
  
  const setAllCountry = useCallback((allCountry: boolean) => {
    dispatch(actions.setAllCountry(allCountry));
  }, [dispatch]);
  
  const setUserImage = useCallback((image: string | null) => {
    dispatch(actions.setUserImage(image));
  }, [dispatch]);
  
  const setSelectedCategoryFilter = useCallback((categoryId: number | null) => {
    dispatch(actions.setSelectedCategoryFilter(categoryId));
  }, [dispatch]);
  
  const setPreferredProfileCategories = useCallback((categoryIds: number[]) => {
    dispatch(actions.setPreferredProfileCategories(categoryIds));
  }, [dispatch]);

  const removePreferredProfileCategory = useCallback((categoryId: number) => {
    const current = state.preferredProfileCategories || [];
    dispatch(actions.setPreferredProfileCategories(current.filter((id: number) => id !== categoryId)));
  }, [dispatch, state.preferredProfileCategories]);
  
  const setStep = useCallback((step: number) => {
    dispatch(actions.setStep(step as any));
  }, [dispatch]);
  
  const resetOnboarding = useCallback(() => {
    dispatch(actions.resetOnboarding());
  }, [dispatch]);
  
  return {
    state,
    loadSkillsAndCategories,
    getCities,
    updateProfile,
    setAccountType,
    setPersonaForm,
    setEmpresaForm,
    setUserSkills,
    addSkill,
    removeSkill,
    setWorkZones,
    setAllCountry,
    setUserImage,
    setSelectedCategoryFilter,
    setPreferredProfileCategories,
    removePreferredProfileCategory,
    setStep,
    resetOnboarding,
  };
};
